<script setup lang="ts">
import slug from 'limax'
import { debounce } from 'lodash-es'
import { useRating } from '~/composables/useRating'
import type { ISeoHeader } from '~/types/rating'

interface Props {
  contrastBackground?: boolean
}

withDefaults(defineProps<Props>(), {
  contrastBackground: false,
})
definePageMeta({
  scrollToTop: false,
})

const { public: { BASE_URL } } = useRuntimeConfig()
const {
  isLoading,
  city,
  cities,
  companyType,
  companyTypes,
  order,
  search,
  switchOrder,
  companiesRating,
  fetchTypes,
  fetchOrganizations,
  fetchCities,
  fetchWordForms,
  canPaginate,
} = useRating()

const showTop = ref(false)
const fetchOrganizationDebounced = debounce(async () => {
  await fetchOrganizations()
  seoRedirect()
}, 1000)

const params: {
  companyType: string
  city: string
} = useRoute().params
const query = useRoute().query

companyType.value = Number(params.companyType)
city.value = Number(params.city)
isLoading.value = false

await fetchCities()
await fetchTypes()
await fetchOrganizations()

const seoCompanyType = computed(() => {
  return companyTypes.value.find(
    el => el.id === Number(params.companyType),
  )
})
const seoCity = cities.value.find(el => el.id === Number(params.city))

const wordForms = await fetchWordForms(params.city, params.companyType)
const currentYear = new Date().getFullYear()
const ratingSeoHeader: ISeoHeader = {
  title: {
    meta: `Рейтинг лучших ${wordForms.type.regularWordForm.genitive.toLowerCase()} ${wordForms.city.regularWordForm.genitive} в ${currentYear}`,
    content: `Рейтинг ${wordForms.type.regularWordForm.genitive.toLowerCase()} ${wordForms.city.regularWordForm.genitive}`,
  },
  description: {
    meta: `Рейтинг ${wordForms.type.regularWordForm.genitive.toLowerCase()} в ${wordForms.city.regularWordForm.prepositional} по данным Яндекс.Карт в ${currentYear} году. Сравните лучшие ${wordForms.type.shortWordForm.accusative.toLowerCase()} по отзывам, качеству работ и ценам.`,
    content: [
      'Мы составили рейтинг компаний по оценкам и отзывам в картах. По этим показателям люди выбирают компании. Принято считать, что чем выше оценка и чем больше отзывов в картах, тем лучше сервис.',
      'На этой странице — общий рейтинг компаний в городе. По клику на каждую компанию — её рейтинг среди компаний по соседству. Он важен, потому что обычно люди выбирают из 10–20 компаний возле дома или работы и сравнивают их по отзывам и оценкам.',
    ],
  },
}

function seoRedirect() {
  if (companyTypes.value && cities.value) {
    if (seoCompanyType.value && seoCity) {
      showTop.value
        = search.value.length > 3 ? false : seoCompanyType.value.group_by_top
      history.pushState(
        {},
        '',
        `/rating/${params.city}/${params.companyType}/${slug(seoCity.name)}/${slug(seoCompanyType.value.name)}${query.org ? `?org=${query.org}` : ''}`,
      )
    }
  }
}

useHead({
  title: ratingSeoHeader.title.meta,
  link: [{
    rel: 'canonical',
    href: `${BASE_URL}rating/${params.city}/${params.companyType}/${slug(seoCity.name)}/${slug(seoCompanyType.value.name)}`,
  }],
})
useServerSeoMeta({
  title: ratingSeoHeader.title.meta,
  description: ratingSeoHeader.description.meta,
})

watch(
  () => search.value,
  () => fetchOrganizationDebounced(),
)
watch(
  () => order.value,
  () => fetchOrganizations(),
)

onMounted(async () => {
  seoRedirect()
})
</script>

<template>
  <section>
    <ol class="hidden" itemscope itemtype="https://schema.org/BreadcrumbList">
      <li
        itemprop="itemListElement" itemscope
        itemtype="https://schema.org/ListItem"
      >
        <NuxtLink to="/" itemprop="item">
          <span itemprop="name">Главная</span>
        </NuxtLink>
        <meta itemprop="position" content="1">
      </li>
      <li
        itemprop="itemListElement" itemscope
        itemtype="https://schema.org/ListItem"
      >
        <NuxtLink to="/rating" itemprop="item">
          <span itemprop="name">Рейтинг {{ wordForms.type.regularWordForm.genitive }} {{ wordForms.city.regularWordForm.genitive }}</span>
        </NuxtLink>
        <meta itemprop="position" content="2">
      </li>
      <li
        itemprop="itemListElement" itemscope
        itemtype="https://schema.org/ListItem"
      >
        <NuxtLink :to="`/rating/${params.city}/${params.companyType}/${slug(seoCity.name)}/${slug(seoCompanyType.name)}`" itemprop="item">
          <span itemprop="name">{{ wordForms.type.regularWordForm.nominative }} в {{ wordForms.city.regularWordForm.prepositional }}</span>
        </NuxtLink>
        <meta itemprop="position" content="3">
      </li>
    </ol>

    <div :class="{ 'bg-milky-200': contrastBackground }" class="w-full">
      <div
        class="max-w-[1012px] w-full px-4 mx-auto py-1 md:py-[40px] flex flex-col gap-6 md:gap-10"
      >
        <RatingHeader
          :title="ratingSeoHeader.title.content"
          :description="ratingSeoHeader.description.content"
        />
        <div class="flex flex-col gap-6">
          <h3 class="hidden md:block">
            Выберите город, категорию и введите название своей компании в поиск,
            чтобы увидеть её позицию в рейтинге:
          </h3>
          <div v-if="!(cities && companyTypes)" class="flex flex-col gap-5">
            <rSkeleton class="w-full h-[70px] bg-gray-200" />
            <rSkeleton class="w-full h-[70px] bg-gray-200" />
          </div>
          <div v-else class="flex flex-col gap-5 text-sm md:text-base">
            <!-- <RTags v-model="city" :is-loading="isLoading" :tags="cities" />
            <RTags
              v-model="companyType"
              :is-loading="isLoading"
              :tags="companyTypes"
            /> -->

            <!-- TODO: decompose to RTags -->
            <!-- Cities -->
            <div class="flex gap-2 flex-wrap">
              <NuxtLink
                v-for="item in cities"
                :key="item.id"
                :to="`/rating/${item.id}/${companyType}/${slug(item.name)}/${slug(companyTypes.find((el) => el.id === companyType).name)}`"
                class="duration-200"
                :class="[{ 'opacity-50 pointer-events-none': isLoading }]"
                @click="isLoading = true"
              >
                <div
                  :class="{
                    'bg-yellow-500': city === item.id,
                    'bg-secondary-100': city !== item.id,
                  }"
                  class="flex flex-row flex-nowrap gap-3 items-center px-4 py-1 rounded-2xl cursor-pointer duration-150 select-none"
                >
                  <span class="whitespace-nowrap">
                    {{ item.name }}
                  </span>
                </div>
              </NuxtLink>
            </div>
            <!-- Companytypes -->
            <div class="flex gap-2 flex-wrap">
              <NuxtLink
                v-for="item in companyTypes"
                :key="item.id"
                :to="`/rating/${city}/${item.id}/${slug(cities.find((el) => el.id === city).name)}/${slug(item.name)}`"
                class="duration-200"
                :class="[{ 'opacity-50 pointer-events-none': isLoading }]"
                @click="isLoading = true"
              >
                <div
                  :class="{
                    'bg-yellow-500': companyType === item.id,
                    'bg-secondary-100': companyType !== item.id,
                  }"
                  class="flex flex-row flex-nowrap gap-3 items-center px-4 py-1 rounded-2xl cursor-pointer duration-150 select-none"
                >
                  <span class="whitespace-nowrap">
                    {{ item.name }}
                  </span>
                </div>
              </NuxtLink>
            </div>
          </div>
          <div class="relative">
            <input
              v-model="search"
              required
              :disabled="isLoading"
              class="w-full max-w-full text-black border px-3 h-[48px] rounded-md hover:border hover:border-black focus:outline-0 focus:border-black disabled:text-secondary-500 disabled:hover:cursor-not-allowed disabled:hover:border-transparent duration-150"
              placeholder="Например, Астра Дент"
              type="text"
            >
            <div
              class="absolute inset-y-0 end-0 flex items-center pe-3 pointer-events-none"
            >
              <Icon
                name="i-ph-magnifying-glass"
                class="text-[28px]"
                :class="{
                  'text-blue-500': !isLoading,
                  'text-blue-300': isLoading,
                }"
              />
            </div>
          </div>
        </div>

        

        <RatingMain
          v-if="companyType && city"
          :data="companiesRating"
          :show-top="showTop"
          :is-loading="isLoading"
          :is-ordered="order"
          :can-paginate="canPaginate"
          @switch-order="switchOrder"
          @next-page="fetchOrganizations(true)"
        />
        <p
          v-else
          class="text-2xl font-bold text-white bg-[#383D44] py-10 rounded-md text-center"
        >
          Данные отсутствуют, выберите город и категорию
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss"></style>
